import type { Location } from "@mmh/types"
import { createSharedComposable } from "@vueuse/core"
import { computed, inject } from "vue"
import { normalizeLocationPath } from "./locations"
import { RestaurantShimKey, useApiConfig } from "./useApiConfig"

/**
 * Deep merge two objects, with config taking precedence over fetched data.
 * Arrays are replaced entirely (not merged).
 * Null/undefined in config will override fetched.
 */
function deepMerge(fetched: any, config: any): any {
    if (!config) return fetched
    if (!fetched) return config
    
    // If config is not an object or is an array, replace entirely
    if (typeof config !== "object" || Array.isArray(config)) {
        return config
    }
    
    const result = { ...fetched }
    
    for (const key in config) {
        if (Object.prototype.hasOwnProperty.call(config, key)) {
            const configValue = config[key]
            const fetchedValue = fetched[key]
            
            // If both are objects (and not arrays), recursively merge
            if (
                configValue && 
                typeof configValue === "object" && 
                !Array.isArray(configValue) &&
                fetchedValue &&
                typeof fetchedValue === "object" &&
                !Array.isArray(fetchedValue)
            ) {
                result[key] = deepMerge(fetchedValue, configValue)
            } else {
                // Otherwise, config value takes precedence
                result[key] = configValue
            }
        }
    }
    
    return result
}

function applyShimToLocation(fetchedLocation: any, configLocation: any) {
    return deepMerge(fetchedLocation, configLocation)
}

export const useConcept = createSharedComposable(() => {
    const { concept: fetchedConcept } = useApiConfig()
    const configShim = inject(RestaurantShimKey, null)

    const concept = computed(() => {
        if (!configShim) return fetchedConcept.value
        
        if (!fetchedConcept.value?.locations) {
            return fetchedConcept.value
        }

        const mergedLocations = configShim.locations
            .map((configLocation: any) => {
                const fetchedLocation = fetchedConcept.value.locations.find(
                    (l: Location) => normalizeLocationPath(l.path) === configLocation.path
                )
                if (!fetchedLocation) return null
                
                // TODO: Remove once database is updated to use the correct paths
                // PATCH: Fix 'costa-mesa' path in prod db to match config path of 'costamesa
                const normalizedFetchedLocation = {
                    ...fetchedLocation,
                    path: normalizeLocationPath(fetchedLocation.path)
                }
                return applyShimToLocation(normalizedFetchedLocation, configLocation)
            })
            .filter(Boolean)

        const { locations: _, ...conceptLevelConfigProps } = configShim
        
        return {
            ...fetchedConcept.value,
            ...conceptLevelConfigProps,
            locations: mergedLocations
        }
    })

    return { concept }
})
